import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, merge } from 'lodash';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { MeasurementToolsService } from '../../services/measurement-tools/measurement-tools.service';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { ArrayModel, JsonArrayModel } from '../complex/array.model';
import { CompositeModel } from '../complex/composite.model';
import { CrmCodeModel, ODataCrmCodeOutModel } from '../complex/crm-code.model';
import { EquipmentModel } from '../complex/equipment.model';
import { RadioModel } from '../complex/radio.model';
import {
    JsonMeasurementToolModel,
    MeasurementToolModel,
    MeasurementToolModelFromJson,
    ODataMeasurementToolOutModel,
} from '../low-level/measurement-tool.model';
import { BooleanModelOptions } from '../options/boolean-model-options';
import { MeasurementToolListOptions } from '../options/measurement-tool-list-options';
import { RadioModelOptions } from '../options/radio-model-options';
import { BooleanModel } from '../primitive/boolean.model';
import { TextModel } from '../primitive/text.model';
import { JsonRadioActiveModel, RadioActiveModel, RadioActiveModelFromJson } from '../low-level/radio-active.model';
import moment from 'moment';
import { NotificationModel } from '../low-level/notification.model';

// Check if the VDE questions need to be displayed for a given business division
function showVDE701702(country: string, processIndicator: ConfigConstants.PROCESS_INDICATOR): boolean {
    return (
        country === ConfigConstants.configKeyGermany && processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO
    );
}

export class ConfirmationMeasurementToolsModel extends CompositeModel {
    protected clientManager: ClientManagerInterface;

    public measurementTools: ArrayModel<MeasurementToolModel>;
    public noMeasurementTool: DisplayDecorator<BooleanModel>;
    public options: MeasurementToolListOptions;

    public showIEC62353 = false;
    public legalCheckIEC62353Text: string;
    public legalCheckIEC62353: RadioModel<TextModel>;
    public legalCheckPCResistance: TextModel;
    public legalCheckDeviceLeakage: TextModel;
    public configLegalCheckPCResistanceText: string;
    public configLegalCheckDeviceLeakageText: string;

    public showVDE701702 = false;
    public legalCheckVDE701702: RadioModel<TextModel>;
    public legalCheckVDEPCResistance: TextModel;
    public legalCheckVDEDeviceLeakage: TextModel;
    public legalCheckVDEInsulationResistance: TextModel;
    public configLegalCheckVDEPCResistanceText: string;
    public configLegalCheckVDEDeviceLeakageText: string;
    public configLegalCheckVDEInsulationResistanceText: string;

    public showXRayCheck = false;
    public legalCheckXRay: DisplayDecorator<BooleanModel>;

    public showDosageCheck = false;
    public legalCheckDosageText = '';
    public legalCheckDosage: RadioModel<TextModel>;

    public showLegalCheck3551 = false;
    public legalCheck3551DetachablePowerCordText: string;
    public legalCheck3551PermanentlyConnectedText: string;
    public legalCheck3551NonDetachablePowerCordText: string;
    public legalCheck3551DetachablePowerCord: RadioModel<TextModel>;
    public legalCheck3551PermanentlyConnected: RadioModel<TextModel>;
    public legalCheck3551NonDetachablePowerCord: RadioModel<TextModel>;

    public showLegalCheckVerification = false;
    public legalCheckVerifiedSecurityDeviceText: string;
    public legalCheckResumptionAfterUserValidationText: string;
    public legalCheckVerifiedSecurityDevice: RadioModel<TextModel>;
    public legalCheckResumptionAfterUserValidation: RadioModel<TextModel>;

    public showRadioProtection = false;
    public legalCheckRadioProtection: DisplayDecorator<BooleanModel>;
    public legalCheckRadioProtectionAcceptanceTest: DisplayDecorator<BooleanModel>;
    public legalCheckRadioProtectionPartialAcceptanceTest: DisplayDecorator<BooleanModel>;
    public legalCheckRadioProtectionHealthCheck: DisplayDecorator<BooleanModel>;
    public legalCheckRadioProtectionConsistencyTest: DisplayDecorator<BooleanModel>;

    public showNEN1010 = false;
    public legalCheckNEN1010: RadioModel<TextModel>;
    public legalCheckNEN1010Radio: RadioModel<TextModel>;
    public legalCheckNEN1010Resistance: TextModel;
    public configLegalCheckInsulationResistanceText: string;
    public configLegalCheckMeasurementPerformedCode: string;

    public radioActiveModelList: RadioActiveModel[] = [];
    public radioActiveToolsFromBackend: RadioActiveModel[] = [];
    public showRadioActiveSurvey = false;
    public radioActiveSurvey: RadioModel<TextModel>;
    public radioActiveAssessmentCompleteText: string;

    public unSelectedEntries: Array<DisplayDecorator<TextModel>> = [];
    public showMeasurementToolsDetails: boolean;
    private readonly measurementToolsService: MeasurementToolsService;
    public constructor(
        notificationType: ConfigConstants.NotificationTypes,
        processIndicator: ConfigConstants.PROCESS_INDICATOR,
        isApplicationSpecialist: boolean,
        equipment: EquipmentModel | undefined,
        measurementTools: MeasurementToolModel[] = [],
        notificationModel?: NotificationModel | undefined,
    ) {
        super('ConfirmationMeasurementToolsModel');
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const translate = injector.get(TranslateService);
        const configService = injector.get(ConfigService);
        this.measurementToolsService = injector.get(MeasurementToolsService);
        this.clientManager = injector.get(ClientManagerInstanceToken);
        const isDefaultSettingsEnabled =
            isApplicationSpecialist && configService.getMeasurementToolsDefaultSettingsAPPS();

        const noMeasurementToolSelected = new BooleanModel('NoMeasurementToolSelected', isDefaultSettingsEnabled);
        this.noMeasurementTool = new DisplayDecorator<BooleanModel>(
            <string>translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_NO_MEASUREMENT_TOOLS_USED'),
            noMeasurementToolSelected,
        );
        // load the NoMeasurementToolSelected field properties
        // for AU and NZ we have default value as X which by default show selected checkbox
        modelOptionsLoader.load(noMeasurementToolSelected, 'MeasurementTools', 'NoMeasurementToolSelected');
        this.showMeasurementToolsDetails = configService.getShowMeasurementToolsDetails();

        // If no measurement tool list then create one
        if (measurementTools.length === 0) {
            if (this.showMeasurementToolsDetails) {
                const entries = this.measurementToolsService.getOriginalDropdownEntries();
                measurementTools.push(new MeasurementToolModel('', false, entries));
                this.setDropdownEntries(entries);
            } else {
                // If no measurement tool list then create one
                // Add new measurement tool as default
                measurementTools.push(new MeasurementToolModel());
            }
        }

        this.options = new MeasurementToolListOptions();
        modelOptionsLoader.load(this, 'MeasurementTools', 'ID');

        this.measurementTools = <ArrayModel<MeasurementToolModel>>(
            this.set(new ArrayModel<MeasurementToolModel>('MeasEquiSet', measurementTools))
        );

        if (equipment && this.clientManager.isNgmP58()) {
            const cseCountry = configService.getCseCountry();
            // IEC6235: Displayed for all below listed country, but only certain business divisions and in-vivo
            // DE,CH,NL,SE,FL,NO,DK,LT,LV,EE,PL,HR,BA,RS,BG,RO,
            // For Czechia CZ - both In-vivo and In-vitro
            this.showIEC62353 =
                (processIndicator === ConfigConstants.PROCESS_INDICATOR.INVIVO ||
                    cseCountry === ConfigConstants.configKeyCzechia) &&
                configService.getDivisionsWithIEC62353(equipment.division.value) &&
                !!configService.getLegalCheckIEC62353Text();

            // VDE701/702: Displayed for country (DE), but only certain business divisions and in-vitro
            this.showVDE701702 =
                showVDE701702(cseCountry, processIndicator) &&
                configService.getDivisionsWithVDE701702(equipment.division.value) &&
                !!configService.getLegalCheckVDE701702Text();

            // additional legal texts needs to display in DE or CH for an equipment of certain division.
            const showAdditionalLegalTexts = configService.getDivisionsWithAdditionalLegalText(
                equipment.division.value,
            );

            // Xray check/ §16 RöV: Only shown for some divisions in Germany
            this.showXRayCheck = cseCountry === ConfigConstants.configKeyGermany && showAdditionalLegalTexts;

            // Dosage check: Only shown for some divisions in Spain
            this.showDosageCheck = cseCountry === ConfigConstants.configKeySpain && showAdditionalLegalTexts;

            // Radio protection: Only shown for some divisions in Switzerland
            this.showRadioProtection = cseCountry === ConfigConstants.configKeySwitzerland && showAdditionalLegalTexts;

            // NEN1010
            this.showNEN1010 = cseCountry === ConfigConstants.configKeyNetherlands; // these questions are always shown in the Netherlands.*/

            // Tested in accordance with AS/NZS 3551:2012
            this.showLegalCheck3551 =
                configService.getLegal3551DetachPowerCordCode().length > 0 &&
                configService.getLegal3551NotificationTypes(notificationType);

            // Verified security device, Resumption after user validation
            this.showLegalCheckVerification =
                !!configService.getLegalVerifiedSecurityDeviceText() &&
                configService.getLegalVerificationNotificationTypes(notificationType) &&
                configService.getDivisionsWithVerificationLegalCheck(equipment.division.value);

            const radioOptionsGroup: Array<DisplayDecorator<TextModel>> = [];
            radioOptionsGroup.push(
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_LEGAL_CHECK_QUESTION_YES'),
                    new TextModel(ConfigConstants.optionYes, ConfigConstants.optionValueYes),
                ),
            );
            radioOptionsGroup.push(
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_LEGAL_CHECK_QUESTION_NO'),
                    new TextModel(ConfigConstants.optionNo, ConfigConstants.optionValueNo),
                ),
            );
            radioOptionsGroup.push(
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_LEGAL_CHECK_QUESTION_NA'),
                    new TextModel(ConfigConstants.optionNA, ConfigConstants.optionValueNA),
                ),
            );
            const optionNoIndex = 1;
            const optionNAIndex = 2;
            const defaultRadioSelection = isDefaultSettingsEnabled ? optionNoIndex : optionNAIndex;

            // From NDP, we get city in <city>;<State>format (Ex: Chicago;IL), we should extract state(IL) and compare
            if (typeof notificationModel !== 'undefined') {
                const stateCode = notificationModel.address.city.value.split(';')[1];
                this.showRadioActiveSurvey =
                    configService.getRadioactiveSurveyEnabled() &&
                    configService.getRadioactiveSurveyDivisions(equipment.division.value) &&
                    configService.getRadioactiveSurveyRegion(stateCode);

                if (this.showRadioActiveSurvey) {
                    this.radioActiveSurvey = new RadioModel<TextModel>(
                        'RadioActiveSurvey',
                        radioOptionsGroup,
                        new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
                    );
                    this.radioActiveAssessmentCompleteText = <string>(
                        translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_RADIO_ACTIVE_ASSESSMENT_COMPLETE')
                    );
                    if (typeof notificationModel.radioActiveSurveyModel !== 'undefined') {
                        this.radioActiveToolsFromBackend = cloneDeep(notificationModel.radioActiveSurveyModel);
                        this.radioActiveModelList.push(...this.radioActiveToolsFromBackend);
                    }
                }
            }

            if (this.showIEC62353) {
                this.legalCheckIEC62353Text = configService.getLegalCheckIEC62353Text();
                this.configLegalCheckPCResistanceText = configService.getLegalCheckPCResistanceText();
                this.configLegalCheckDeviceLeakageText = configService.getLegalCheckDeviceLeakageText();

                const configLegalCheckIEC62353Code = configService.getLegalCheckIEC62353Code();
                const configLegalCheckPCResistanceCode = configService.getLegalCheckPCResistanceCode();
                const configLegalCheckDeviceLeakageCode = configService.getLegalCheckDeviceLeakageCode();

                this.legalCheckIEC62353 = new RadioModel<TextModel>(
                    configLegalCheckIEC62353Code,
                    radioOptionsGroup,
                    new RadioModelOptions({ initialSelection: defaultRadioSelection, invalidSelection: 2 }),
                );

                this.legalCheckPCResistance = new TextModel(configLegalCheckPCResistanceCode);
                modelOptionsLoader.load(this.legalCheckPCResistance, 'Equipment', 'PCResistance');
                this.legalCheckPCResistance = assignModelOptions(
                    this.legalCheckPCResistance,
                    {
                        ...this.legalCheckPCResistance.options,
                        unit: ConfigConstants.INPUT_FIELD_UNITS.mOhm,
                    },
                    this,
                );

                this.legalCheckDeviceLeakage = new TextModel(configLegalCheckDeviceLeakageCode);
                modelOptionsLoader.load(this.legalCheckDeviceLeakage, 'Equipment', 'DeviceLeakage');
                this.legalCheckDeviceLeakage = assignModelOptions(
                    this.legalCheckDeviceLeakage,
                    {
                        ...this.legalCheckDeviceLeakage.options,
                        unit: ConfigConstants.INPUT_FIELD_UNITS.mA,
                    },
                    this,
                );
            }

            if (this.showXRayCheck) {
                const configLegalCheckXRayText = `${configService.getLegalCheckXRayText()} ${translate.instant(
                    'CONFIRMATION_MEASUREMENT_TOOLS_XRAY_CHECK_TEXT',
                )}`;

                const configLegalCheckXRayCode = configService.getLegalCheckXRayCode();
                this.legalCheckXRay = new DisplayDecorator(
                    configLegalCheckXRayText,
                    new BooleanModel(configLegalCheckXRayCode),
                );
            }

            if (this.showDosageCheck) {
                this.legalCheckDosageText = configService.getLegalCheckDosageText();
                this.legalCheckDosage = new RadioModel<TextModel>(
                    configService.getLegalCheckDosageCode(),
                    radioOptionsGroup,
                    new RadioModelOptions({ initialSelection: defaultRadioSelection, invalidSelection: 2 }),
                );
            }

            if (this.showRadioProtection) {
                const configLegalCheckRadioProtectionText = configService.getLegalCheckRadioProtectionText();
                const configLegalCheckRadioProtectionCode = configService.getLegalCheckRadioProtectionCode();

                const configLegalCheckAcceptanceTestCode = configService.getLegalCheckAcceptanceTestCode();
                const configLegalCheckPartialAcceptanceTestCode =
                    configService.getLegalCheckPartialAcceptanceTestCode();
                const configLegalCheckHealthCheckCode = configService.getLegalCheckHealthCheckCode();
                const configLegalCheckConsistencyTestCode = configService.getLegalCheckConsistencyTestCode();

                this.legalCheckRadioProtection = new DisplayDecorator(
                    configLegalCheckRadioProtectionText,
                    new BooleanModel(configLegalCheckRadioProtectionCode),
                );
                this.legalCheckRadioProtectionAcceptanceTest = new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_RADIO_PROTECTION_ACCEPTANCE_TEXT'),
                    new BooleanModel(
                        configLegalCheckAcceptanceTestCode,
                        false,
                        new BooleanModelOptions({ required: true }),
                    ),
                );
                this.legalCheckRadioProtectionPartialAcceptanceTest = new DisplayDecorator(
                    <string>(
                        translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_RADIO_PROTECTION_PARTIAL_ACCEPTANCE_TEXT')
                    ),
                    new BooleanModel(
                        configLegalCheckPartialAcceptanceTestCode,
                        false,
                        new BooleanModelOptions({ required: true }),
                    ),
                );
                this.legalCheckRadioProtectionHealthCheck = new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_RADIO_PROTECTION_HEALTH_CHECK_TEXT'),
                    new BooleanModel(
                        configLegalCheckHealthCheckCode,
                        false,
                        new BooleanModelOptions({ required: true }),
                    ),
                );
                this.legalCheckRadioProtectionConsistencyTest = new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_RADIO_PROTECTION_CONSISTENCY_TEXT'),
                    new BooleanModel(
                        configLegalCheckConsistencyTestCode,
                        false,
                        new BooleanModelOptions({ required: true }),
                    ),
                );
            }

            if (this.showNEN1010) {
                const configLegalCheckNEN1010Code = configService.getLegalCheckNEN1010Code();

                const configLegalCheckK3RoomText = configService.getLegalCheckK3RoomText();
                const configLegalCheckMeasurementPerformedText = configService.getLegalCheckMeasurementPerformedText();
                const configLegalCheckMeasurementNotNeededText = configService.getLegalCheckMeasurementNotNeededText();
                this.configLegalCheckInsulationResistanceText = configService.getLegalCheckInsulationResistanceText();

                const configLegalCheckK3RoomCode = configService.getLegalCheckK3RoomCode();
                this.configLegalCheckMeasurementPerformedCode = configService.getLegalCheckMeasurementPerformedCode();
                const configLegalCheckMeasurementNotNeededCode = configService.getLegalCheckMeasurementNotNeededCode();
                const configLegalCheckInsulationResistanceCode = configService.getLegalCheckInsulationResistanceCode();

                this.legalCheckNEN1010 = new RadioModel<TextModel>(
                    configLegalCheckNEN1010Code,
                    radioOptionsGroup,
                    new RadioModelOptions({ initialSelection: defaultRadioSelection, invalidSelection: 2 }),
                );

                const checkNEN1010Options: Array<DisplayDecorator<TextModel>> = [];
                checkNEN1010Options.push(
                    new DisplayDecorator(
                        configLegalCheckK3RoomText,
                        new TextModel(configLegalCheckK3RoomCode, configLegalCheckK3RoomCode),
                    ),
                );
                checkNEN1010Options.push(
                    new DisplayDecorator(
                        configLegalCheckMeasurementPerformedText,
                        new TextModel(
                            this.configLegalCheckMeasurementPerformedCode,
                            this.configLegalCheckMeasurementPerformedCode,
                        ),
                    ),
                );
                checkNEN1010Options.push(
                    new DisplayDecorator(
                        configLegalCheckMeasurementNotNeededText,
                        new TextModel(
                            configLegalCheckMeasurementNotNeededCode,
                            configLegalCheckMeasurementNotNeededCode,
                        ),
                    ),
                );
                const optionNA = 'NA';
                checkNEN1010Options.push(
                    new DisplayDecorator(
                        <string>translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_NEN1010_OPTION_NA'),
                        new TextModel(optionNA, optionNA),
                    ),
                );

                this.legalCheckNEN1010Radio = new RadioModel<TextModel>(
                    'NEN1010Checklist',
                    checkNEN1010Options,
                    new RadioModelOptions({ initialSelection: 3, invalidSelection: 3 }),
                );
                this.legalCheckNEN1010Resistance = new TextModel(configLegalCheckInsulationResistanceCode);
                modelOptionsLoader.load(this.legalCheckNEN1010Resistance, 'Equipment', 'NEN1010Resistance');
                this.legalCheckNEN1010Resistance = assignModelOptions(
                    this.legalCheckNEN1010Resistance,
                    {
                        ...this.legalCheckNEN1010Resistance.options,
                        unit: ConfigConstants.INPUT_FIELD_UNITS.MOhm,
                    },
                    this,
                );
            }

            // Legal checks for VDE701/702
            if (this.showVDE701702) {
                this.configLegalCheckVDEPCResistanceText = configService.getLegalCheckVDEPCResistanceText();
                this.configLegalCheckVDEDeviceLeakageText = configService.getLegalCheckVDEDeviceLeakageText();
                this.configLegalCheckVDEInsulationResistanceText =
                    configService.getLegalCheckVDEInsulationResistanceText();
                this.legalCheckVDE701702 = new RadioModel<TextModel>(
                    configService.getLegalCheckVDE701702Code(),
                    radioOptionsGroup,
                    new RadioModelOptions({ initialSelection: defaultRadioSelection, invalidSelection: 2 }),
                );
                this.legalCheckVDEPCResistance = new TextModel(configService.getLegalCheckVDEPCResistanceCode());
                modelOptionsLoader.load(this.legalCheckVDEPCResistance, 'Equipment', 'VDEPCResistance');
                this.legalCheckVDEPCResistance = assignModelOptions(this.legalCheckVDEPCResistance, {
                    ...this.legalCheckVDEPCResistance.options,
                    unit: ConfigConstants.INPUT_FIELD_UNITS.mOhm,
                });

                this.legalCheckVDEDeviceLeakage = new TextModel(configService.getLegalCheckVDEDeviceLeakageCode());
                modelOptionsLoader.load(this.legalCheckVDEDeviceLeakage, 'Equipment', 'VDEDeviceLeakage');
                this.legalCheckVDEDeviceLeakage = assignModelOptions(this.legalCheckVDEDeviceLeakage, {
                    ...this.legalCheckVDEDeviceLeakage.options,
                    unit: ConfigConstants.INPUT_FIELD_UNITS.mA,
                });

                this.legalCheckVDEInsulationResistance = new TextModel(
                    configService.getLegalCheckVDEInsulationResistanceCode(),
                );
                modelOptionsLoader.load(this.legalCheckVDEInsulationResistance, 'Equipment', 'VDEInsulationResistance');
                this.legalCheckVDEInsulationResistance = assignModelOptions(this.legalCheckVDEInsulationResistance, {
                    ...this.legalCheckVDEInsulationResistance.options,
                    unit: ConfigConstants.INPUT_FIELD_UNITS.MOhm,
                });
            }

            // Legal checks for Australia/New Zealand
            if (this.showLegalCheck3551) {
                this.legalCheck3551DetachablePowerCordText = configService.getLegal3551DetachPowerCordText();
                this.legalCheck3551PermanentlyConnectedText = configService.getLegal3551PermanentConnectedText();
                this.legalCheck3551NonDetachablePowerCordText = configService.getLegal3551NonDetachPowerCordText();

                const configLegalCheck3551DetachablePowerCordCode = configService.getLegal3551DetachPowerCordCode();
                const configLegalCheck3551PermanentlyConnectedCode = configService.getLegal3551PermanentConnectedCode();
                const configLegalCheck3551NonDetachablePowerCordCode =
                    configService.getLegal3551NonDetachPowerCordCode();

                this.legalCheck3551DetachablePowerCord = new RadioModel<TextModel>(
                    configLegalCheck3551DetachablePowerCordCode,
                    radioOptionsGroup,
                    new RadioModelOptions({ initialSelection: defaultRadioSelection, invalidSelection: 2 }),
                );
                this.legalCheck3551PermanentlyConnected = new RadioModel<TextModel>(
                    configLegalCheck3551PermanentlyConnectedCode,
                    radioOptionsGroup,
                    new RadioModelOptions({ initialSelection: defaultRadioSelection, invalidSelection: 2 }),
                );
                this.legalCheck3551NonDetachablePowerCord = new RadioModel<TextModel>(
                    configLegalCheck3551NonDetachablePowerCordCode,
                    radioOptionsGroup,
                    new RadioModelOptions({ initialSelection: defaultRadioSelection, invalidSelection: 2 }),
                );
            }

            if (this.showLegalCheckVerification) {
                this.legalCheckVerifiedSecurityDeviceText = configService.getLegalVerifiedSecurityDeviceText();
                const configLegalCheckVerifiedSecurityDeviceCode = configService.getLegalVerifiedSecurityDeviceCode();
                this.legalCheckResumptionAfterUserValidationText =
                    configService.getLegalResumptionAfterUserValidationText();
                const configLegalResumptionAfterUserValidationCode =
                    configService.getLegalResumptionAfterUserValidationCode();
                this.legalCheckVerifiedSecurityDevice = new RadioModel<TextModel>(
                    configLegalCheckVerifiedSecurityDeviceCode,
                    radioOptionsGroup,
                    new RadioModelOptions({ initialSelection: defaultRadioSelection, invalidSelection: 2 }),
                );
                this.legalCheckResumptionAfterUserValidation = new RadioModel<TextModel>(
                    configLegalResumptionAfterUserValidationCode,
                    radioOptionsGroup,
                    new RadioModelOptions({ initialSelection: defaultRadioSelection, invalidSelection: 2 }),
                );
            }
        }
    }

    public setRadioActiveSurvey(radioActiveModels?: RadioActiveModel[]): void {
        if (radioActiveModels && radioActiveModels.length > 0) {
            this.radioActiveModelList = cloneDeep(radioActiveModels);
        } else {
            this.radioActiveModelList = [new RadioActiveModel()];
        }
    }
    /**
     * we need these getter in HTML since we have two container to show two different content
     * If yes selected, show Radio action table
     * If No, selected, Show plain text "No Radioactive Sources Used"
     */
    public get isRadioActiveSelectedYes(): boolean {
        return (
            this.radioActiveSurvey &&
            this.radioActiveSurvey.selectedEntry &&
            this.radioActiveSurvey.selectedEntry.model.value === ConfigConstants.optionValueYes
        );
    }

    public get isRadioActiveSelectedNo(): boolean {
        return (
            this.radioActiveSurvey &&
            this.radioActiveSurvey.selectedEntry &&
            this.radioActiveSurvey.selectedEntry.model.value === ConfigConstants.optionValueNo
        );
    }

    public getOriginalEntries(): Array<DisplayDecorator<TextModel>> {
        return this.measurementToolsService.getOriginalDropdownEntries();
    }

    public getAllSelectedTool(): Array<DisplayDecorator<TextModel>> {
        return this.measurementTools.value.map((tool) => tool.dropdownValues.selectedEntry);
    }

    public technicalIdAlreadyAvailable(techId: string, index: number): boolean {
        return this.measurementTools.value.some(
            (model, i) =>
                i !== index && model.technicalId.value.trim().toLocaleLowerCase() === techId.trim().toLocaleLowerCase(),
        );
    }

    /**
     * Scenario
     * If user click on No measurement tool checkbox and enables it again then we have to revert all the changes
     * Also need to set the dropdown values to original state
     */
    public revertDropdownValues(): void {
        const list = this.measurementToolsService.getOriginalDropdownEntries();
        this.setDropdownEntries(list);
    }

    public setDropdownEntries(entries: Array<DisplayDecorator<TextModel>>): void {
        this.unSelectedEntries = cloneDeep(this.measurementToolsService.sortByGid(entries));
        this.measurementToolsService.setDropdownEntries(this.unSelectedEntries);
    }

    public customiseAllDropdownEntry(
        currentEntry: string,
        previousEntry: DisplayDecorator<TextModel> | undefined,
    ): void {
        const index = this.measurementTools.value.findIndex(
            (tool) =>
                tool.dropdownValues.selectedEntry?.model.value.toLocaleLowerCase() ===
                currentEntry.trim().toLocaleLowerCase(),
        );
        let unSelectedTools: Array<DisplayDecorator<TextModel>> = [];
        this.measurementTools.value.forEach((toolModel, i) => {
            if (previousEntry && previousEntry.model.value !== currentEntry) {
                const isPreviousEntryExist = toolModel.isToolAvailableInDropdown(previousEntry.model.value);
                if (!isPreviousEntryExist) {
                    toolModel.addToolToDropdown(previousEntry);
                }
            }
            if (index !== i) {
                toolModel.removeToolFromDropdown(currentEntry);
                if (!toolModel.technicalId.value) {
                    toolModel.setDropdownVisibility(
                        toolModel.dropdownValues.value.length > 0 && !toolModel.textDescription.value,
                    );
                }
            }
            unSelectedTools = toolModel.getUnselectedTools();
        });
        // also set the unselected entries to the list
        this.setDropdownEntries(unSelectedTools);
    }

    /**
     * It returns measurement tool dropdown entries
     *
     * @returns the unselected entries
     */
    public getUnSelectedDropdownEntries(): Array<DisplayDecorator<TextModel>> {
        return this.measurementToolsService.sortByGid(this.unSelectedEntries);
    }

    /**
     * It loops each measurement tool model that added in confirmation screen and add the tool in all dropdown
     *
     * @param tool - the tool to be added
     */
    public addToolInEachDropdown(tool: DisplayDecorator<TextModel>): void {
        this.measurementTools.value.forEach((model) => {
            model.addToolToDropdown(tool);
            if (!model.technicalId.value) {
                model.setDropdownVisibility(!model.textDescription.value && model.dropdownValues.value.length > 0);
            }
        });
    }

    public addNewMeasurementToolModel(): void {
        if (this.showMeasurementToolsDetails) {
            const dropdownEntries = cloneDeep(this.getUnSelectedDropdownEntries());
            this.measurementTools.addModel(new MeasurementToolModel('', false, dropdownEntries));
            return;
        }
        this.measurementTools.addModel(new MeasurementToolModel());
    }

    private static getEquipmentLegalCheckCrmCodeModel(code: string, codeText: string): CrmCodeModel {
        return new CrmCodeModel({
            codeType: 'M',
            codeGroup: 'IEC6',
            code,
            codeText,
        });
    }

    private getRadioActiveSelectedCrmModel(code: string, codeText: string): CrmCodeModel {
        return new CrmCodeModel({
            codeType: 'A',
            codeGroup: 'IEC6',
            code,
            codeText,
            startDateTime: moment().startOf('minute'),
            endDateTime: moment().startOf('minute'),
        });
    }

    /**
     * Get measurement tool outmodel based on "No Measurement Tool" selection
     *
     * @returns the outmodel
     */
    public getOutModel(): ODataConfirmationWithOrWithoutMeasurementToolsOutModel {
        // get the outmodel
        const outModel = !this.noMeasurementTool.value
            ? <ODataConfirmationMeasurementToolsOutModel>super.getOutModel()
            : {};
        if (this.clientManager.isNgmP58()) {
            // CrmCodes subentity for legal texts
            const crmCodes = <ArrayModel<CrmCodeModel>>this.set(new ArrayModel<CrmCodeModel>('CrmCodesSet'));
            if (
                this.showIEC62353 &&
                this.legalCheckIEC62353.selectedEntry &&
                this.legalCheckIEC62353.selectedEntry.value === ConfigConstants.optionValueYes
            ) {
                crmCodes.addModel(
                    ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                        this.legalCheckIEC62353.name,
                        ConfigConstants.sapTrue,
                    ),
                );
                if (this.legalCheckPCResistance.value) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheckPCResistance.name,
                            this.legalCheckPCResistance.value.toString(),
                        ),
                    );
                }

                if (this.legalCheckDeviceLeakage.value) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheckDeviceLeakage.name,
                            this.legalCheckDeviceLeakage.value.toString(),
                        ),
                    );
                }
            }
            if (this.showXRayCheck && this.legalCheckXRay.value) {
                crmCodes.addModel(
                    ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                        this.legalCheckXRay.name,
                        ConfigConstants.sapTrue,
                    ),
                );
            }
            if (this.showDosageCheck && this.legalCheckDosage.selectedEntry) {
                crmCodes.addModel(
                    ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                        this.legalCheckDosage.name,
                        this.legalCheckDosage.selectedEntry.name,
                    ),
                );
            }
            if (this.showRadioProtection && this.legalCheckRadioProtection.value) {
                crmCodes.addModel(
                    ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                        this.legalCheckRadioProtection.name,
                        ConfigConstants.sapTrue,
                    ),
                );
                if (this.legalCheckRadioProtectionAcceptanceTest.value) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheckRadioProtectionAcceptanceTest.name,
                            ConfigConstants.sapTrue,
                        ),
                    );
                }
                if (this.legalCheckRadioProtectionPartialAcceptanceTest.value) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheckRadioProtectionPartialAcceptanceTest.name,
                            ConfigConstants.sapTrue,
                        ),
                    );
                }
                if (this.legalCheckRadioProtectionHealthCheck.value) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheckRadioProtectionHealthCheck.name,
                            ConfigConstants.sapTrue,
                        ),
                    );
                }
                if (this.legalCheckRadioProtectionConsistencyTest.value) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheckRadioProtectionConsistencyTest.name,
                            ConfigConstants.sapTrue,
                        ),
                    );
                }
            }
            if (
                this.showNEN1010 &&
                this.legalCheckNEN1010.selectedEntry &&
                this.legalCheckNEN1010.selectedEntry.value === ConfigConstants.optionValueYes
            ) {
                crmCodes.addModel(
                    ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                        this.legalCheckNEN1010.name,
                        ConfigConstants.sapTrue,
                    ),
                );
                crmCodes.addModel(
                    ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                        this.legalCheckNEN1010Radio.selectedEntry.value,
                        ConfigConstants.sapTrue,
                    ),
                );
                if (this.legalCheckNEN1010Radio.selectedEntry.value === this.configLegalCheckMeasurementPerformedCode) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheckNEN1010Resistance.name,
                            this.legalCheckNEN1010Resistance.value
                                ? this.legalCheckNEN1010Resistance.value.toString()
                                : '',
                        ),
                    );
                }
            }
            if (
                this.showVDE701702 &&
                this.legalCheckVDE701702.selectedEntry &&
                this.legalCheckVDE701702.selectedEntry.value === ConfigConstants.optionValueYes
            ) {
                crmCodes.addModel(
                    ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                        this.legalCheckVDE701702.name,
                        ConfigConstants.sapTrue,
                    ),
                );
                if (this.legalCheckVDEPCResistance.value) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheckVDEPCResistance.name,
                            this.legalCheckVDEPCResistance.value.toString(),
                        ),
                    );
                }
                if (this.legalCheckVDEDeviceLeakage.value) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheckVDEDeviceLeakage.name,
                            this.legalCheckVDEDeviceLeakage.value.toString(),
                        ),
                    );
                }
                if (this.legalCheckVDEInsulationResistance.value) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheckVDEInsulationResistance.name,
                            this.legalCheckVDEInsulationResistance.value.toString(),
                        ),
                    );
                }
            }
            if (this.showLegalCheck3551) {
                if (this.legalCheck3551DetachablePowerCord.selectedEntry.value === ConfigConstants.optionValueYes) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheck3551DetachablePowerCord.name,
                            ConfigConstants.sapTrue,
                        ),
                    );
                }
                if (this.legalCheck3551PermanentlyConnected.selectedEntry.value === ConfigConstants.optionValueYes) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheck3551PermanentlyConnected.name,
                            ConfigConstants.sapTrue,
                        ),
                    );
                }
                if (this.legalCheck3551NonDetachablePowerCord.selectedEntry.value === ConfigConstants.optionValueYes) {
                    crmCodes.addModel(
                        ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                            this.legalCheck3551NonDetachablePowerCord.name,
                            ConfigConstants.sapTrue,
                        ),
                    );
                }
            }
            if (this.showLegalCheckVerification) {
                crmCodes.addModel(
                    ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                        this.legalCheckVerifiedSecurityDevice.name,
                        this.legalCheckVerifiedSecurityDevice.selectedEntry.value === ConfigConstants.optionValueYes
                            ? ConfigConstants.sapTrue
                            : '',
                    ),
                );
                crmCodes.addModel(
                    ConfirmationMeasurementToolsModel.getEquipmentLegalCheckCrmCodeModel(
                        this.legalCheckResumptionAfterUserValidation.name,
                        this.legalCheckResumptionAfterUserValidation.selectedEntry.value ===
                            ConfigConstants.optionValueYes
                            ? ConfigConstants.sapTrue
                            : '',
                    ),
                );
            }
            if (!this.noMeasurementTool.value && this.showRadioActiveSurvey && this.radioActiveSurvey) {
                const codeText = this.isRadioActiveSelectedYes ? ConfigConstants.sapTrue : ConfigConstants.sapFalse;
                const code = this.isRadioActiveSelectedYes ? 'US01' : 'US02';

                crmCodes.addModel(this.getRadioActiveSelectedCrmModel(code, codeText));

                if (this.isRadioActiveSelectedYes) {
                    // CrmCodes for all each radio active entry
                    this.radioActiveModelList.forEach((radioActiveModel) => {
                        crmCodes.addModel(radioActiveModel.getRadioActiveSurveyCrmCodeModel());
                    });
                    // Another CrmCodeModel with plane text, "Radiological Assessment Complete"
                    crmCodes.addModel(
                        this.getRadioActiveSelectedCrmModel(code, this.radioActiveAssessmentCompleteText),
                    );
                }
            }
            merge(outModel, crmCodes.getOutModel());
        }
        return outModel;
    }

    public resetRequiredOption(): void {
        if (this.showIEC62353) {
            const isNoValueEntered = !this.legalCheckPCResistance.value && !this.legalCheckDeviceLeakage.value;
            this.legalCheckDeviceLeakage = assignModelOptions(this.legalCheckDeviceLeakage, {
                ...this.legalCheckDeviceLeakage.options,
                required: isNoValueEntered,
            });
            this.legalCheckPCResistance = assignModelOptions(this.legalCheckPCResistance, {
                ...this.legalCheckPCResistance.options,
                required: isNoValueEntered,
            });
        }
        if (this.showVDE701702) {
            // If code VDE1 (checkbox) is checked it is mandatory to fill out at least one value in VDE2, VDE3 or VDE4
            this.legalCheckVDEPCResistance = assignModelOptions(this.legalCheckVDEPCResistance, {
                ...this.legalCheckVDEPCResistance.options,
                required: !this.legalCheckVDEDeviceLeakage.value && !this.legalCheckVDEInsulationResistance.value,
            });
            this.legalCheckVDEDeviceLeakage = assignModelOptions(this.legalCheckVDEDeviceLeakage, {
                ...this.legalCheckVDEDeviceLeakage.options,
                required: !this.legalCheckVDEPCResistance.value && !this.legalCheckVDEInsulationResistance.value,
            });
            this.legalCheckVDEInsulationResistance = assignModelOptions(this.legalCheckVDEInsulationResistance, {
                ...this.legalCheckVDEInsulationResistance.options,
                required: !this.legalCheckVDEPCResistance.value && !this.legalCheckVDEDeviceLeakage.value,
            });
        }
        if (this.showRadioProtection) {
            const isAnyRadioProtectionCheckSelected =
                this.legalCheckRadioProtection.value &&
                (this.legalCheckRadioProtectionAcceptanceTest.value ||
                    this.legalCheckRadioProtectionPartialAcceptanceTest.value ||
                    this.legalCheckRadioProtectionHealthCheck.value ||
                    this.legalCheckRadioProtectionConsistencyTest.value);
            this.legalCheckRadioProtectionAcceptanceTest = assignModelOptions(
                this.legalCheckRadioProtectionAcceptanceTest,
                {
                    ...this.legalCheckRadioProtectionAcceptanceTest.options,
                    required: !isAnyRadioProtectionCheckSelected,
                },
                this,
            );
            this.legalCheckRadioProtectionPartialAcceptanceTest = assignModelOptions(
                this.legalCheckRadioProtectionPartialAcceptanceTest,
                {
                    ...this.legalCheckRadioProtectionPartialAcceptanceTest.options,
                    required: !isAnyRadioProtectionCheckSelected,
                },
                this,
            );
            this.legalCheckRadioProtectionHealthCheck = assignModelOptions(
                this.legalCheckRadioProtectionHealthCheck,
                {
                    ...this.legalCheckRadioProtectionHealthCheck.options,
                    required: !isAnyRadioProtectionCheckSelected,
                },
                this,
            );
            this.legalCheckRadioProtectionConsistencyTest = assignModelOptions(
                this.legalCheckRadioProtectionConsistencyTest,
                {
                    ...this.legalCheckRadioProtectionConsistencyTest.options,
                    required: !isAnyRadioProtectionCheckSelected,
                },
                this,
            );
        }
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns the JSON representation of the model
     */
    public toJSON(): unknown {
        return {
            measurementTools: this.measurementTools,
            noMeasurementTool: this.noMeasurementTool.model.value,
            legalCheckIEC62353: this.showIEC62353 ? this.legalCheckIEC62353.selectedEntry.value : '',
            legalCheckPCResistance: this.legalCheckPCResistance ? this.legalCheckPCResistance.value : '',
            legalCheckDeviceLeakage: this.legalCheckDeviceLeakage ? this.legalCheckDeviceLeakage.value : '',
            legalCheckNEN1010: this.legalCheckNEN1010 ? this.legalCheckNEN1010.selectedEntry.value : '',
            legalCheckNEN1010Radio: this.legalCheckNEN1010Radio ? this.legalCheckNEN1010Radio.selectedEntry.value : '',
            legalCheckNEN1010Resistance: this.legalCheckNEN1010Resistance ? this.legalCheckNEN1010Resistance.value : '',
            legalCheckRadioProtection: this.legalCheckRadioProtection ? this.legalCheckRadioProtection.value : false,
            legalCheckRadioProtectionAcceptanceTest: this.legalCheckRadioProtectionAcceptanceTest
                ? this.legalCheckRadioProtectionAcceptanceTest.value
                : false,
            legalCheckRadioProtectionPartialAcceptanceTest: this.legalCheckRadioProtectionPartialAcceptanceTest
                ? this.legalCheckRadioProtectionPartialAcceptanceTest.value
                : false,
            legalCheckRadioProtectionHealthCheck: this.legalCheckRadioProtectionHealthCheck
                ? this.legalCheckRadioProtectionHealthCheck.value
                : false,
            legalCheckRadioProtectionConsistencyTest: this.legalCheckRadioProtectionConsistencyTest
                ? this.legalCheckRadioProtectionConsistencyTest.value
                : false,
            legalCheckVDE701702: this.showVDE701702 ? this.legalCheckVDE701702.selectedEntry.value : '',
            legalCheckVDEPCResistance: this.legalCheckVDEPCResistance ? this.legalCheckVDEPCResistance.value : '',
            legalCheckVDEDeviceLeakage: this.legalCheckVDEDeviceLeakage ? this.legalCheckVDEDeviceLeakage.value : '',
            legalCheckVDEInsulationResistance: this.legalCheckVDEInsulationResistance
                ? this.legalCheckVDEInsulationResistance.value
                : '',
            legalCheckXRay: this.legalCheckXRay ? this.legalCheckXRay.value : false,
            legalCheckDosage: this.legalCheckDosage ? this.legalCheckDosage.selectedEntry.value : '',
            legalCheck3551DetachablePowerCord: this.legalCheck3551DetachablePowerCord
                ? this.legalCheck3551DetachablePowerCord.selectedEntry.value
                : '',
            legalCheck3551PermanentlyConnected: this.legalCheck3551PermanentlyConnected
                ? this.legalCheck3551PermanentlyConnected.selectedEntry.value
                : '',
            legalCheck3551NonDetachablePowerCord: this.legalCheck3551NonDetachablePowerCord
                ? this.legalCheck3551NonDetachablePowerCord.selectedEntry.value
                : '',
            legalCheckVerifiedSecurityDevice: this.legalCheckVerifiedSecurityDevice
                ? this.legalCheckVerifiedSecurityDevice.selectedEntry.value
                : '',
            legalCheckResumptionAfterUserValidation: this.legalCheckResumptionAfterUserValidation
                ? this.legalCheckResumptionAfterUserValidation.selectedEntry.value
                : '',
            radioActivitySurveyValue: this.radioActiveSurvey?.selectedEntry?.model.value,
            radioActivityModel: this.radioActiveModelList,
        };
    }
}

export class ConfirmationMeasurementToolsModelFromJson extends ConfirmationMeasurementToolsModel {
    public constructor(
        notificationType: ConfigConstants.NotificationTypes,
        processIndicator: ConfigConstants.PROCESS_INDICATOR,
        isApplicationSpecialist: boolean,
        equipmentModel: EquipmentModel | undefined,
        data: JsonConfirmationMeasurementToolsModel,
        notificationModel: NotificationModel,
    ) {
        super(
            notificationType,
            processIndicator,
            isApplicationSpecialist,
            equipmentModel,
            (<JsonMeasurementToolModel[]>data.measurementTools.value).map((jsonTool) => {
                return new MeasurementToolModelFromJson(jsonTool);
            }),
            notificationModel,
        );
        if (this.showMeasurementToolsDetails) {
            this.measurementTools.value = (<JsonMeasurementToolModel[]>data.measurementTools.value).map((jsonTool) => {
                return new MeasurementToolModelFromJson(jsonTool);
            });
        }

        if (this.clientManager.isNgmP58()) {
            if (this.showIEC62353) {
                this.legalCheckIEC62353.selectByPredicate((entry) => entry.value === data.legalCheckIEC62353);
                this.legalCheckDeviceLeakage.value = data.legalCheckDeviceLeakage;
                this.legalCheckPCResistance.value = data.legalCheckPCResistance;
            }
            if (this.showNEN1010) {
                this.legalCheckNEN1010.selectByPredicate((entry) => entry.value === data.legalCheckNEN1010);
                this.legalCheckNEN1010Radio.selectByPredicate((entry) => entry.value === data.legalCheckNEN1010Radio);
                if (this.legalCheckNEN1010Resistance && data.legalCheckNEN1010Resistance) {
                    this.legalCheckNEN1010Resistance.value = data.legalCheckNEN1010Resistance;
                }
            }
            if (this.showRadioProtection) {
                this.legalCheckRadioProtection.value = data.legalCheckRadioProtection;
                this.legalCheckRadioProtectionAcceptanceTest.value = data.legalCheckRadioProtectionAcceptanceTest;
                this.legalCheckRadioProtectionPartialAcceptanceTest.value =
                    data.legalCheckRadioProtectionPartialAcceptanceTest;
                this.legalCheckRadioProtectionHealthCheck.value = data.legalCheckRadioProtectionHealthCheck;
                this.legalCheckRadioProtectionConsistencyTest.value = data.legalCheckRadioProtectionConsistencyTest;
            }
            if (this.showVDE701702) {
                this.legalCheckVDE701702.selectByPredicate((entry) => entry.value === data.legalCheckVDE701702);
                this.legalCheckVDEPCResistance.value = data.legalCheckVDEPCResistance;
                this.legalCheckVDEDeviceLeakage.value = data.legalCheckVDEDeviceLeakage;
                this.legalCheckVDEInsulationResistance.value = data.legalCheckVDEInsulationResistance;
            }
            if (this.showXRayCheck) {
                this.legalCheckXRay.value = data.legalCheckXRay;
            }
            if (this.showDosageCheck) {
                this.legalCheckDosage.selectByPredicate((entry) => entry.value === data.legalCheckDosage);
            }
            if (this.showLegalCheck3551) {
                this.legalCheck3551DetachablePowerCord.selectByPredicate(
                    (entry) => entry.value === data.legalCheck3551DetachablePowerCord,
                );
                this.legalCheck3551PermanentlyConnected.selectByPredicate(
                    (entry) => entry.value === data.legalCheck3551PermanentlyConnected,
                );
                this.legalCheck3551NonDetachablePowerCord.selectByPredicate(
                    (entry) => entry.value === data.legalCheck3551NonDetachablePowerCord,
                );
            }
            if (this.showLegalCheckVerification) {
                this.legalCheckVerifiedSecurityDevice.selectByPredicate(
                    (entry) => entry.value === data.legalCheckResumptionAfterUserValidation,
                );
                this.legalCheckResumptionAfterUserValidation.selectByPredicate(
                    (entry) => entry.value === data.legalCheckResumptionAfterUserValidation,
                );
            }
        }

        this.noMeasurementTool.model.value = data.noMeasurementTool;
        this.resetRequiredOption();
        if (data.radioActivityModel.length > 0 && data.radioActivitySurveyValue) {
            const savedRadioActiveModel = data.radioActivityModel.map(
                (radioActiveModel) => new RadioActiveModelFromJson(radioActiveModel),
            );
            this.setRadioActiveSurvey(savedRadioActiveModel);
            this.radioActiveSurvey?.selectByPredicate((entry) => entry.value === data.radioActivitySurveyValue);
        }
    }
}

export interface JsonConfirmationMeasurementToolsModel {
    measurementTools: JsonArrayModel;
    noMeasurementTool: boolean;
    legalCheckIEC62353: string;
    legalCheckPCResistance: string;
    legalCheckDeviceLeakage: string;
    legalCheckNEN1010: string;
    legalCheckNEN1010Radio: string;
    legalCheckNEN1010Resistance: string;
    legalCheckRadioProtection: boolean;
    legalCheckRadioProtectionAcceptanceTest: boolean;
    legalCheckRadioProtectionPartialAcceptanceTest: boolean;
    legalCheckRadioProtectionHealthCheck: boolean;
    legalCheckRadioProtectionConsistencyTest: boolean;
    legalCheckVDE701702: string;
    legalCheckVDEPCResistance: string;
    legalCheckVDEDeviceLeakage: string;
    legalCheckVDEInsulationResistance: string;
    legalCheckXRay: boolean;
    legalCheck3551DetachablePowerCord: string;
    legalCheck3551PermanentlyConnected: string;
    legalCheck3551NonDetachablePowerCord: string;
    legalCheckVerifiedSecurityDevice: string;
    legalCheckResumptionAfterUserValidation: string;
    legalCheckDosage: string;
    radioActivitySurveyValue: string;
    radioActivityModel: JsonRadioActiveModel[];
}

export interface ODataConfirmationMeasurementToolsOutModel extends ODataObject {
    MeasEquiSet: ODataMeasurementToolOutModel[];
    CrmCodesSet: ODataCrmCodeOutModel[];
}

export type ODataConfirmationWithOrWithoutMeasurementToolsOutModel =
    | ODataConfirmationMeasurementToolsOutModel
    | Record<string, never>;
